import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Package, 
  Plus, 
  Search, 
  Edit2, 
  Trash2,
  AlertTriangle,
  Filter,
  Bell,
  Mail,
  Upload,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultCategories = ["Volvo", "Detroit", "Fleetguard", "General", "Filters", "Belts", "Electrical"];

export default function Inventory() {
  const { theme } = useTheme();
  const { businessSlug } = useParams();
  const [searchParams] = useSearchParams();
  const isDark = theme === "dark";
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(defaultCategories);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  // Stock-level filter — lets the owner narrow the inventory list to
  // just the items that need restocking (or out of stock). Front-desk
  // safe (no dollar figures involved).
  const [stockFilter, setStockFilter] = useState(() => {
    // Honor ?stock=low / ?stock=out from the dashboard "View all" link
    // so clicking it lands the user on a pre-filtered restock view.
    const v = (typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("stock") : null);
    return v && ["low", "out", "in", "all"].includes(v) ? v : "all";
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [markup, setMarkup] = useState({ type: "amount", value: 30 });
  const [sendingAlert, setSendingAlert] = useState(false);
  
  const API = `${BASE_API}/b/${businessSlug}`;
  
  const [formData, setFormData] = useState({
    part_number: "",
    name: "",
    category: "",
    quantity: 0,
    cost: 0,
    price: 30,
    min_stock: 5,
    price_locked: false,
    // Defaults ON. When OFF, this part is "pass-through" — adding it to an
    // invoice won't contribute to "Profit from Parts" on the dashboard.
    // Useful for parts where the price varies by customer (e.g. customer-
    // supplied parts, hot-shot items priced ad-hoc per job).
    track_profit: true,
  });

  useEffect(() => {
    if (businessSlug) {
      fetchInventory();
      fetchCategories();
      fetchSettings();
    }
  }, [businessSlug]);

  // Sync stock filter when the URL `?stock=` query changes (e.g. the
  // user clicked the dashboard's "View all" link).
  useEffect(() => {
    const v = searchParams.get("stock");
    if (v && ["low", "out", "in", "all"].includes(v)) setStockFilter(v);
  }, [searchParams]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.markup) {
        setMarkup(response.data.markup);
        return response.data.markup;
      }
    } catch (error) {
      // Use defaults
    }
    return markup;
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/inventory`);
      setItems(response.data);
    } catch (error) {
      toast.error("Failed to fetch inventory");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/inventory/categories/list`);
      if (response.data.categories.length > 0) {
        const validCategories = response.data.categories.filter(cat => cat && cat.trim() !== '');
        setCategories([...new Set([...defaultCategories, ...validCategories])]);
      }
    } catch (error) {
      console.error("Error fetching categories");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/inventory/${editingItem.id}`, formData);
        toast.success("Item updated successfully");
      } else {
        await axios.post(`${API}/inventory`, formData);
        toast.success("Item added successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchInventory();
    } catch (error) {
      toast.error(editingItem ? "Failed to update item" : "Failed to add item");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API}/inventory/${id}`);
      toast.success("Item deleted");
      fetchInventory();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleSendLowStockAlert = async () => {
    setSendingAlert(true);
    try {
      const response = await axios.post(`${API}/inventory/low-stock/send-alert`);
      if (response.data.count > 0) {
        toast.success(`Alert sent for ${response.data.count} low stock items`);
      } else {
        toast.info("No low stock items to alert about");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send alert");
    } finally {
      setSendingAlert(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      part_number: item.part_number,
      name: item.name,
      category: item.category,
      quantity: item.quantity,
      cost: item.cost || 0,
      price: item.price,
      min_stock: item.min_stock,
      price_locked: item.price_locked || false,
      track_profit: item.track_profit !== false,
    });
    setIsDialogOpen(true);
  };

  // Calculate sale price based on markup settings
  const calculateSalePrice = (cost, markupSettings = markup) => {
    if (markupSettings.type === "percentage") {
      return cost * (1 + markupSettings.value / 100);
    }
    return cost + markupSettings.value;
  };

  const resetForm = (markupSettings = markup) => {
    setEditingItem(null);
    setFormData({
      part_number: "",
      name: "",
      category: "",
      quantity: 0,
      cost: 0,
      price: calculateSalePrice(0, markupSettings),
      min_stock: 5,
      price_locked: false,
      track_profit: true,
    });
  };

  const thresholdFor = (item) => item.min_stock ?? item.low_stock_threshold ?? 5;

  const filteredItems = items.filter((item) => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.part_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    let matchesStock = true;
    if (stockFilter === "low") {
      // At or below threshold, but still has at least 1 in stock.
      matchesStock = item.quantity > 0 && item.quantity <= thresholdFor(item);
    } else if (stockFilter === "out") {
      matchesStock = item.quantity <= 0;
    } else if (stockFilter === "in") {
      matchesStock = item.quantity > thresholdFor(item);
    }
    return matchesSearch && matchesCategory && matchesStock;
  });

  const lowStockCount = items.filter(item => item.quantity > 0 && item.quantity <= thresholdFor(item)).length;
  const outOfStockCount = items.filter(item => item.quantity <= 0).length;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="inventory-loading">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            Inventory
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {items.length} parts in stock • {lowStockCount} low stock
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Import — links to the bulk upload page with category preselected.
              Surfaced here per user request: "remove Import Data from
              sidebar, implement it where it needs to be — click
              Inventory → see Import Inventory + explain how it works". */}
          <Link to={`/${businessSlug}/import?type=inventory`}>
            <Button
              variant="outline"
              className={isDark ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : ''}
              data-testid="import-inventory-btn"
              title="Bulk-upload a CSV, Excel, or PDF — we'll auto-detect columns (name, SKU, qty, cost, price, category) and add the parts in one shot."
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Inventory
            </Button>
          </Link>
          {lowStockCount > 0 && (
            <Button 
              variant="outline"
              onClick={handleSendLowStockAlert}
              disabled={sendingAlert}
              className={`${isDark ? 'border-amber-700 text-amber-400 hover:bg-amber-900/30' : 'border-amber-300 text-amber-600 hover:bg-amber-50'}`}
              data-testid="send-low-stock-alert-btn"
            >
              {sendingAlert ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Send Low Stock Alert
                </>
              )}
            </Button>
          )}
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            onClick={async () => { 
              const freshMarkup = await fetchSettings();
              resetForm(freshMarkup); 
              setIsDialogOpen(true); 
            }}
            data-testid="add-item-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Part
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search parts by name or part number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                data-testid="search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className={`w-full md:w-48 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.filter(cat => cat && cat.trim() !== '').map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={stockFilter} onValueChange={setStockFilter}>
              <SelectTrigger className={`w-full md:w-48 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid="stock-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Stock level" />
              </SelectTrigger>
              <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                <SelectItem value="all" data-testid="stock-filter-all">All items</SelectItem>
                <SelectItem value="low" data-testid="stock-filter-low">
                  Low stock {lowStockCount > 0 && `(${lowStockCount})`}
                </SelectItem>
                <SelectItem value="out" data-testid="stock-filter-out">
                  Out of stock {outOfStockCount > 0 && `(${outOfStockCount})`}
                </SelectItem>
                <SelectItem value="in" data-testid="stock-filter-in">In stock (healthy)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700' : 'border-slate-200'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Part #</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Name</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Category</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Qty</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Cost</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Sale Price</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No parts found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => (
                    <TableRow 
                      key={item.id} 
                      className={`table-row-hover ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                      data-testid={`inventory-row-${item.id}`}
                    >
                      <TableCell className="font-mono text-sm text-blue-600">{item.part_number}</TableCell>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        <div className="flex items-center gap-2">
                          {item.name}
                          {item.quantity <= item.min_stock && (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={isDark ? 'border-slate-600 text-slate-400' : 'border-slate-200 text-slate-500'}>
                          {item.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={item.quantity <= item.min_stock ? "text-amber-600 font-bold" : isDark ? "text-white" : "text-slate-800"}>
                          {item.quantity}
                        </span>
                      </TableCell>
                      <TableCell className={`text-right ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        {formatCurrency(item.cost)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-green-600">
                        <div className="flex items-center justify-end gap-1">
                          {formatCurrency(item.price)}
                          {item.price_locked && <span className="text-amber-500 text-xs">🔒</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(item)}
                            className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-slate-700"
                            data-testid={`edit-item-${item.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(item.id)}
                            className="hover:bg-red-50 hover:text-red-600 dark:hover:bg-slate-700"
                            data-testid={`delete-item-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-800'}>
              {editingItem ? "Edit Part" : "Add New Part"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Part Number</Label>
                <Input
                  value={formData.part_number}
                  onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                  required
                  data-testid="input-part-number"
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'} data-testid="input-category">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                    {categories.filter(cat => cat && cat.trim() !== '').map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Part Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                required
                data-testid="input-name"
              />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Qty</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.quantity === 0 ? '' : formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                  onBlur={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                  placeholder="0"
                  required
                  data-testid="input-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Cost</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cost === 0 ? '' : formData.cost}
                  onChange={(e) => {
                    const val = e.target.value;
                    const cost = val === '' ? 0 : parseFloat(val);
                    setFormData({ ...formData, cost: val === '' ? '' : cost, price: calculateSalePrice(cost) });
                  }}
                  onBlur={(e) => {
                    const cost = parseFloat(e.target.value) || 0;
                    setFormData({ ...formData, cost: cost, price: calculateSalePrice(cost) });
                  }}
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                  placeholder="0.00"
                  required
                  data-testid="input-cost"
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                  Sale $ {formData.price_locked && <span className="text-xs text-amber-500">(Locked)</span>}
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price === 0 ? '' : formData.price}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    price: e.target.value === '' ? '' : parseFloat(e.target.value),
                    price_locked: true  // Mark as locked when user manually edits
                  })}
                  onBlur={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  className={`${formData.price_locked ? 'text-amber-600 border-amber-400' : 'text-green-600'} ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                  placeholder="0.00"
                  required
                  data-testid="input-price"
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Min</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value) || 0 })}
                  className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                  data-testid="input-min-stock"
                />
              </div>
            </div>

            {/* Lock Price Toggle */}
            {editingItem && (
              <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    Lock Price
                  </p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    {formData.price_locked 
                      ? "This price won't change when markup is updated" 
                      : "Price will auto-update with global markup changes"}
                  </p>
                </div>
                <Button
                  type="button"
                  variant={formData.price_locked ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (formData.price_locked) {
                      // Reset to calculated price
                      const calculatedPrice = calculateSalePrice(formData.cost || 0);
                      setFormData({ ...formData, price_locked: false, price: calculatedPrice });
                    } else {
                      setFormData({ ...formData, price_locked: true });
                    }
                  }}
                  className={formData.price_locked ? 'bg-amber-500 hover:bg-amber-600' : ''}
                >
                  {formData.price_locked ? "🔒 Locked" : "Auto"}
                </Button>
              </div>
            )}

            {/* Track Profit Toggle — controls whether this part contributes
                to "Profit from Parts" on the dashboard. Off = pass-through
                (price varies by customer, no profit math). */}
            <div className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`} data-testid="track-profit-toggle-row">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  Track profit
                </p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {formData.track_profit
                    ? "Counts toward dashboard parts-profit math (Sale − Cost)"
                    : "Pass-through — price varies per customer, no profit calculated"}
                </p>
              </div>
              <Button
                type="button"
                variant={formData.track_profit ? "default" : "outline"}
                size="sm"
                onClick={() => setFormData({ ...formData, track_profit: !formData.track_profit })}
                className={formData.track_profit ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
                data-testid="track-profit-toggle"
              >
                {formData.track_profit ? "✓ Tracked" : "Off"}
              </Button>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className={isDark ? 'border-slate-600' : 'border-slate-200'}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="submit-item-btn"
              >
                {editingItem ? "Update" : "Add Part"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
